import { ApolloClient, ApolloLink, InMemoryCache, fromPromise, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import feathersClient from 'services';
import { createUploadLink } from 'apollo-upload-client';
import * as amplitude from '@amplitude/analytics-browser';
//
import isEmpty from 'lodash/isEmpty';
import { GRAPHQL_SERVER, GRAPHQL_SERVER_WS } from 'config/constants';
import { REFRESH_TOKEN } from './modules/auth';
import {
  ACCESS_TOKEN_JWT_KEY,
  REFRESH_TOKEN_JWT_KEY,
  saveToken,
  TOKEN_SAVED_ON_STORAGE_EVENT_NAME,
} from '../utils/auth';
import { allTypePolicies } from './type-policies';
import { resetOnUnauthenticate } from '../utils/reset-on-unauthenticate';
import * as Sentry from '@sentry/react';
import { SERVER_URL } from '../config/constants';

const httpLink = createUploadLink({
  uri: GRAPHQL_SERVER,
});

const wsLink = new WebSocketLink({
  uri: GRAPHQL_SERVER_WS,
  options: {
    lazy: true,
    timeout: 30000,
    reconnect: true,
    connectionParams: async () => {
      const getAuthenticationHeaders = () => {
        const accessToken = localStorage.getItem(ACCESS_TOKEN_JWT_KEY);
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_JWT_KEY);

        if (!accessToken || !refreshToken) {
          return null;
        }

        return {
          authorization: accessToken,
          'x-refresh-token': refreshToken,
        };
      };

      const authHeaders = getAuthenticationHeaders();
      if (authHeaders) {
        return authHeaders;
      }

      // WebSocketLink can be initialized on app start, when the tokens are not available yet
      // The tokens will only be set on local storage once the app authenticates.
      // The goal of this promise is to await the tokens being saved
      // by listening to the TOKEN_SAVED_ON_STORAGE_EVENT_NAME event
      return new Promise((resolve) => {
        const onTokenSavedOnStorage = () => {
          const authHeaders = getAuthenticationHeaders();

          if (authHeaders) {
            window.removeEventListener(TOKEN_SAVED_ON_STORAGE_EVENT_NAME, onTokenSavedOnStorage);
            return resolve(authHeaders);
          }
        };

        window.addEventListener(TOKEN_SAVED_ON_STORAGE_EVENT_NAME, onTokenSavedOnStorage);
      });
    },
  },
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    const shouldForwardToWS = definition?.name?.value.includes('WS');

    return (
      shouldForwardToWS ||
      (definition.kind === 'OperationDefinition' && definition.operation === 'subscription')
    );
  },
  wsLink,
  httpLink
);

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem(ACCESS_TOKEN_JWT_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_JWT_KEY);

  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token || '',
      'x-refresh-token': refreshToken || '',
    },
  };
});

const getNewToken = async (apolloClient) => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_JWT_KEY) || '';
  if (!refreshToken) {
    return Promise.reject(new Error('Invalid refresh token.'));
  }

  const res = await apolloClient.mutate({
    mutation: REFRESH_TOKEN,
    variables: {
      refreshToken,
    },
  });

  if (!res.data) {
    resetOnUnauthenticate();
  } else {
    const { accessToken } = res.data.refreshToken;

    await feathersClient
      .authenticate({
        strategy: 'jwt',
        accessToken,
      })
      .catch((e) => {
        Sentry.captureMessage('Error attempting to authenticate to FeathersJS Rest Client', {
          extra: {
            flow: 'getNewToken',
            apiType: 'rest',
            url: SERVER_URL,
            errorMessage: e.message,
            errorCode: e.code,
          },
        });

        throw e;
      });
  }

  return res.data.refreshToken;
};

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      switch (err.extensions.code) {
      case 'UNAUTHENTICATED':
        return fromPromise(
          getNewToken(apolloClient).catch(() => {
            localStorage.clear();
            amplitude.reset();
          })
        )
          .filter((value) => !isEmpty(value))
          .flatMap(({ accessToken, refreshToken }) => {
            saveToken({ accessToken, refreshToken });

            const oldHeaders = operation.getContext().headers;
            // modify the operation context with a new token
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: accessToken,
                'x-refresh-token': refreshToken,
              },
            });

            // retry the request, returning the new observable
            return forward(operation);
          });
      }
    }
  }

  if (networkError) {
    console.log('Network error: ', networkError);
  }
});

const cache = new InMemoryCache();

allTypePolicies.forEach(({ typePolicies }) => {
  cache.policies.addTypePolicies(typePolicies);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, splitLink]),
  cache,
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
});
